import { useState, useRef, useEffect, useMemo } from "react";
import { Box, Dialog, DialogContent } from "@mui/material";
import ModalHeader from "./content/ModalHeader";
import ModalFooter from "./content/ModalFooter";
import StepperTabs from "./content/StepperTabs";
import PriceServicesStep from "./Steps/Step1";
import LocationSettingsContent from "./Steps/Step2";
import PricelistAdditionalInformation from "./Steps/Step3";
import ClosePricelistDialog from "../ClosePricelistDialog";
import { useFormik } from "formik";
import { apiPostRequest, apiDeleteRequest } from "../../../../Helpers/Requests";
import endpoints from "../../../../Helpers/Endpoints";
import { useSnackbar } from "../../../../Components/SnackBar";
import PointerUtils from "../../../../Utils/pointer";
import { ActiveOrderModel } from "../ActiveOrdersModal";
import { useTranslation } from "react-i18next";
import { dialogBox, dialogStyle } from "./styles";
import emitter from "../../../../Utils/events/eventBus";

const PricelistModal = ({
  open,
  onClose,
  pricelist,
  categories,
  getPricelist,
  getAllPricelistsData,
  setOpenNewPricelistCreatedModal,
  pricelistsLength,
  pricelists,
}) => {
  const { t } = useTranslation(["pricelist-modal", "categories"]);
  const snackbar = useSnackbar();
  const [activeStep, setActiveStep] = useState(0);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const initialValues = useRef(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [hasPendingPriceModal, setHasPendingPriceModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);

  const nextFileId = useRef(0);

  const checkAnyVariantPriced = (subPricelists) => {
    return (subPricelists || []).some((sub) =>
      sub.services?.some((service) =>
        service.variants?.some(
          (variant) =>
            variant.price !== null &&
            variant.price !== undefined &&
            variant.price !== "" &&
            variant.price > 0
        )
      )
    );
  };

  const [hasAnyVariantPriced, setHasAnyVariantPriced] = useState(() =>
    checkAnyVariantPriced(pricelist?.sub_pricelists)
  );

  const handleVariantPriceChange = (subPricelists) => {
    setHasAnyVariantPriced(checkAnyVariantPriced(subPricelists));
  };

  const isExistingPricelist = useMemo(() => {
    return pricelists.some(
      (item) =>
        item.subcategory_id?.toString() ===
        pricelist?.subcategory_id?.toString()
    );
  }, [pricelists, pricelist?.subcategory_id]);

  const formik = useFormik({
    initialValues: {
      pricelist: {
        subcategory_id: pricelist?.subcategory_id || 0,
        subcategory_name: pricelist?.subcategory_name || "",
        subcategory_image_url: pricelist?.subcategory_image_url || "",
        visit_price: pricelist?.visit_price || "",
        visit_price_relevant: pricelist?.visit_price_relevant || false,
        visit_price_enabled: pricelist?.visit_price_relevant || false,
        currency: pricelist?.currency || "",
        supplier_experience_mandatory:
          pricelist?.supplier_experience_mandatory || false,
        years_of_experience:
          pricelist?.years_of_experience === null ||
          pricelist?.years_of_experience === undefined ||
          pricelist?.years_of_experience === ""
            ? ""
            : pricelist?.years_of_experience || 0,
        sub_pricelists: pricelist?.sub_pricelists || [],
        location: {
          business_address_enabled:
            pricelist?.location?.business_address_enabled || false,
          business_address: pricelist?.location?.business_address || "",
          service_areas_enabled:
            pricelist?.location?.service_areas_enabled || false,
          service_areas: pricelist?.location?.service_areas || [],
          online_enabled: pricelist?.location?.online_enabled || false,
        },
        supplier_files_mandatory: pricelist?.supplier_files_mandatory || false,
        additional_info: {
          additional_details:
            pricelist?.additional_info?.additional_details || "",
          files: pricelist?.additional_info?.files || [],
        },
        has_active_contracts: pricelist?.has_active_contracts || false,
      },
    },
    onSubmit: async (values) => {
      setIsSubmitting(true);
      PointerUtils.disable();

      let uploadedFiles = [];

      try {
        if (filesToDelete.length > 0) {
          await Promise.allSettled(
            filesToDelete.map(async (fileUrl) => {
              try {
                const encodedFileUrl = encodeURIComponent(fileUrl);
                await apiDeleteRequest(
                  `${endpoints.removePricelistFile(
                    values.pricelist.subcategory_id
                  )}?file_url=${encodedFileUrl}`
                );
              } catch (err) {
                console.error("File delete error:", err);
                snackbar(`Failed to delete file: ${fileUrl}`, "error");
              }
            })
          );
        }

        if (pendingFiles.length > 0) {
          const uploadResults = await Promise.allSettled(
            pendingFiles.map((pendingFile) => {
              const formData = new FormData();
              formData.append("file", pendingFile.rawFile);
              return apiPostRequest(
                endpoints.uploadPricelistFile(values.pricelist.subcategory_id),
                formData,
                {
                  "Content-Type": "multipart/form-data",
                }
              );
            })
          );

          uploadedFiles = uploadResults
            .map((result, i) => {
              if (result.status === "fulfilled") {
                return {
                  name: result.value.data.name,
                  url: result.value.data.url,
                };
              } else {
                console.error("Upload error:", result.reason);
                snackbar(
                  `Failed to upload file: ${pendingFiles[i].name}`,
                  "error"
                );
                return null;
              }
            })
            .filter(Boolean);
        }

        const remainingFiles = values.pricelist.additional_info.files.filter(
          (file) => !filesToDelete.includes(file.url)
        );

        const finalFiles = [...remainingFiles, ...uploadedFiles];

        const {
          pricelist: { visit_price_enabled, ...restPricelistValues },
        } = values;

        const dataToSend = {
          ...values,
          pricelist: {
            ...restPricelistValues,
            visit_price:
              restPricelistValues.visit_price === ""
                ? null
                : restPricelistValues.visit_price,
            years_of_experience:
              restPricelistValues.years_of_experience === "" ||
              restPricelistValues.years_of_experience == null
                ? 0
                : restPricelistValues.years_of_experience,
            additional_info: {
              ...restPricelistValues.additional_info,
              files: finalFiles,
            },
          },
        };

        const response = await apiPostRequest(
          endpoints.savePricelist(values.pricelist.subcategory_id),
          dataToSend
        );

        if (response.status === 200) {
          snackbar(
            `${t(values.pricelist.subcategory_name, { ns: "categories" })} ${t(
              "save_message"
            )}`,
            "success"
          );
          initialValues.current = JSON.parse(JSON.stringify(values));
          setIsDirty(false);
          setFilesToDelete([]);

          if (pricelistsLength === 0) {
            emitter.emit("fetch-get-started-content");
            setOpenNewPricelistCreatedModal(true);
          }

          await getPricelist();
          await getAllPricelistsData();
          onClose();
        }
      } catch (error) {
        console.error("Pricelist save failed:", error);
        const errorMessage =
          error?.response?.data?.detail ||
          error?.message ||
          "An unexpected error occurred";
        snackbar(errorMessage, "error");
      } finally {
        PointerUtils.enable();
        setIsSubmitting(false);
        setPendingFiles([]);
        setFilesToDelete([]);
      }
    },
  });

  const valuesRef = useRef({ ...formik.initialValues });

  useEffect(() => {
    if (open && !initialValues.current) {
      initialValues.current = JSON.parse(JSON.stringify(formik.values));
    }
  }, [open, formik.values]);

  useEffect(() => {
    if (initialValues.current) {
      const checkDirty = () => {
        const currentValues = JSON.stringify(formik.values);
        const initialValuesStr = JSON.stringify(initialValues.current);
        setIsDirty(currentValues !== initialValuesStr);
      };
      checkDirty();
    }
  }, [formik.values]);

  const handleNext = () => {
    if (activeStep < 2) setActiveStep(activeStep + 1);
  };

  const handlePrevious = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  const handleSave = async () => {
    PointerUtils.disable();
    const initialSubPricelists =
      initialValues.current?.pricelist?.sub_pricelists || [];

    const currentSubPricelists =
      valuesRef.current.pricelist.sub_pricelists || [];

    formik.setFieldValue(
      "pricelist.sub_pricelists",
      valuesRef.current.pricelist.sub_pricelists || []
    );

    const priceChanges = [];
    const removedVariants = [];

    currentSubPricelists.forEach((currentSub) => {
      const initialSub = initialSubPricelists.find(
        (s) => s.id === currentSub.id
      );

      if (!initialSub) return;

      currentSub.services?.forEach((currentService) => {
        const initialService = initialSub.services?.find(
          (s) => s.id === currentService.id
        );
        if (!initialService) return;

        currentService.variants?.forEach((currentVariant) => {
          const oldVariant = initialService.variants?.find(
            (v) => v.id === currentVariant.id
          );
          if (!oldVariant) return;

          if (
            currentVariant.price !== null &&
            currentVariant.price !== 0 &&
            oldVariant.price !== currentVariant.price
          ) {
            priceChanges.push({
              id: currentVariant.id,
              name: currentVariant.sku,
              old_price: oldVariant.price ?? 0,
              new_price: currentVariant.price ?? 0,
            });
          }
        });
      });
    });

    initialSubPricelists.forEach((initialSub) => {
      const currentSub = currentSubPricelists.find(
        (s) => s.id === initialSub.id
      );

      if (!currentSub) {
        initialSub.services?.forEach((srv) => {
          removedVariants.push(
            ...srv.variants.map((v) => ({
              id: v.id,
              name: v.sku,
            }))
          );
        });
        return;
      }

      initialSub.services?.forEach((initialService) => {
        const currentService = currentSub.services?.find(
          (s) => s.id === initialService.id
        );

        if (!currentService) {
          removedVariants.push(
            ...initialService.variants.map((v) => ({
              id: v.id,
              name: v.sku,
            }))
          );
          return;
        }

        initialService.variants?.forEach((initialVariant) => {
          const currentVariant = currentService.variants?.find(
            (v) => v.id === initialVariant.id
          );

          const initialPrice = initialVariant.price ?? 0;
          const currentPrice = currentVariant?.price ?? 0;

          const hadInitialPrice = initialPrice > 0;
          const isNowRemoved =
            !currentVariant || currentPrice === 0 || currentPrice === null;

          if (hadInitialPrice && isNowRemoved) {
            removedVariants.push({
              id: initialVariant.id,
              name: initialVariant.sku,
            });
          }
        });
      });
    });

    const initialVisitPrice = initialValues.current.pricelist.visit_price ?? 0;
    const currentVisitPrice = formik.values.pricelist.visit_price ?? 0;

    const visitPriceIncreased = currentVisitPrice > initialVisitPrice;

    try {
      let priceResponse = null;
      let removedResponse = null;

      if (removedVariants.length > 0) {
        removedResponse = await apiPostRequest(
          endpoints.checkRemovedVariants(
            formik.values.pricelist.subcategory_id
          ),
          { removed_variants: removedVariants }
        );
      }

      if (priceChanges.length > 0) {
        priceResponse = await apiPostRequest(
          endpoints.checkPriceChanges(formik.values.pricelist.subcategory_id),
          { variants: priceChanges }
        );
      }

      if (removedResponse?.data?.has_active_orders) {
        if (priceResponse?.data?.has_active_orders || visitPriceIncreased) {
          setHasPendingPriceModal(true);
        }
        setModalType("variant");
        setShowModal(true);
      } else if (priceResponse?.data?.has_active_orders) {
        if (visitPriceIncreased) {
          setHasPendingPriceModal(true);
        }
        setModalType("price");
        setShowModal(true);
      } else if (
        priceResponse?.data?.has_active_orders &&
        visitPriceIncreased &&
        isExistingPricelist
      ) {
        setModalType("visit_price");
        setShowModal(true);
      } else {
        formik.handleSubmit();
      }
    } catch (error) {
      console.error("Validation error before save", error);
    } finally {
      PointerUtils.enable();
    }
  };

  const handleCloseClick = () => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const handleCloseConfirm = () => {
    setShowCloseConfirm(false);
    onClose();
  };

  const handleCloseCancel = () => {
    setShowCloseConfirm(false);
  };

  const handleModalConfirm = () => {
    if (
      (modalType === "variant" || modalType === "price") &&
      hasPendingPriceModal
    ) {
      if (modalType === "variant") {
        setModalType("price");
      } else if (modalType === "price") {
        setModalType("visit_price");
      }
      setHasPendingPriceModal(false);
      return;
    }

    setShowModal(false);
    formik.handleSubmit();
  };

  const isAnyLocationSelected = () => {
    const {
      business_address_enabled,
      service_areas_enabled,
      online_enabled,
      business_address,
    } = formik.values.pricelist.location;

    if (business_address_enabled) {
      const isValidAddress =
        business_address &&
        (typeof business_address === "object"
          ? business_address.address && business_address.address.trim() !== ""
          : business_address.trim() !== "");
      return (
        isValidAddress && (service_areas_enabled || online_enabled || true)
      );
    }

    return service_areas_enabled || online_enabled;
  };

  const addPendingFile = (file) => {
    const fileWithId = {
      ...file,
      pendingId: nextFileId.current++,
    };
    setPendingFiles((prev) => [...prev, fileWithId]);
  };

  const removePendingFile = (pendingId) => {
    setPendingFiles((prev) => {
      const fileToRemove = prev.find((f) => f.pendingId === pendingId);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter((f) => f.pendingId !== pendingId);
    });
  };

  const addFileToDelete = (fileId) => {
    setFilesToDelete((prev) => [...prev, fileId]);
  };

  const hasVisitPriceSet = () => {
    const { visit_price, visit_price_enabled, visit_price_relevant } =
      formik.values.pricelist;
    return (
      visit_price_relevant &&
      visit_price_enabled &&
      visit_price !== "" &&
      visit_price != null &&
      visit_price > 0
    );
  };

  const isStep0NextDisabled = () => {
    const { visit_price_relevant } = formik.values.pricelist;
    // If no visit price is relevant, at least 1 variant must be priced
    if (!visit_price_relevant) {
      return !hasAnyVariantPriced;
    }
    // If visit price is relevant, Next is always enabled
    return false;
  };

  const isSaveDisabled = () => {
    const {
      values: {
        pricelist: {
          supplier_experience_mandatory,
          years_of_experience,
          visit_price,
          visit_price_enabled,
          visit_price_relevant,
          supplier_files_mandatory,
          additional_info,
        },
      },
    } = formik;

    // If toggle is on, visit price is mandatory
    if (
      visit_price_relevant &&
      visit_price_enabled &&
      (visit_price === "" || visit_price == null)
    ) {
      return true;
    }

    // At least 1 item must be priced (visit price or variant)
    if (!hasVisitPriceSet() && !hasAnyVariantPriced) {
      return true;
    }

    // Check years of experience requirement
    if (
      supplier_experience_mandatory &&
      (years_of_experience === "" || years_of_experience === null)
    ) {
      return true;
    }

    // Check files requirement only if mandatory
    if (supplier_files_mandatory) {
      const existingFiles = additional_info.files || [];
      const newFilesToBeUploaded = pendingFiles.length;

      if (existingFiles.length === 0 && newFilesToBeUploaded === 0) {
        return true;
      }
      return false;
    }

    return false;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleCloseClick();
        }}
        disableEscapeKeyDown
        fullWidth
        maxWidth="1200px"
        PaperProps={{ sx: dialogStyle }}
      >
        <Box sx={dialogBox}>
          <ModalHeader
            title={
              `${pricelist?.subcategory_name}` ||
              `${t("create_a_new")} ${t("pricelist")}`
            }
            onClose={handleCloseClick}
            steps={<StepperTabs activeStep={activeStep} />}
            activeStep={activeStep}
          />
          <DialogContent
            sx={{
              p: 0,
              mr: "5px",
            }}
            className="custom-scrollbar"
          >
            <Box sx={{ p: { xs: "16px", sm: "32px" }, borderRadius: 2 }}>
              {activeStep === 0 && (
                <PriceServicesStep
                  {...{ valuesRef, onVariantPriceChange: handleVariantPriceChange }}
                />
              )}
              {activeStep === 1 && <LocationSettingsContent {...{ formik }} />}
              {activeStep === 2 && (
                <PricelistAdditionalInformation
                  {...{
                    formik,
                    addPendingFile,
                    removePendingFile,
                    addFileToDelete,
                    pendingFiles,
                    requiredFilesText: pricelist?.required_files_text || "",
                    hasAnyVariantPriced,
                  }}
                />
              )}
            </Box>
          </DialogContent>
          <ModalFooter
            onNext={handleNext}
            onPrevious={activeStep === 0 ? handleCloseClick : handlePrevious}
            activeStep={activeStep}
            onSave={handleSave}
            formik={formik}
            categories={categories}
            isNextDisabled={!isAnyLocationSelected()}
            isStep0NextDisabled={isStep0NextDisabled()}
            isSaveDisabled={isSaveDisabled()}
            isSubmitting={isSubmitting}
            isExistingPricelist={isExistingPricelist}
          />
        </Box>
      </Dialog>

      <ClosePricelistDialog
        open={showCloseConfirm}
        onClose={handleCloseCancel}
        onConfirm={handleCloseConfirm}
      />

      <ActiveOrderModel
        open={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleModalConfirm}
        title={
          modalType === "price"
            ? t("active_orders_prices")
            : modalType === "visit_price"
            ? t("active_orders_visit_price_title")
            : t("active_orders")
        }
        boldText={
          modalType === "price" || modalType === "visit_price"
            ? t("active_orders_remain_the_same")
            : t("active_orders_remain")
        }
        description={
          modalType === "price"
            ? t("you_updated_prices_some_varaiants")
            : modalType === "visit_price"
            ? t("you_updated_visit_price")
            : t("you_removed_some_varaiants")
        }
      />
    </>
  );
};

export default PricelistModal;
